import React, { useState, useRef, useEffect } from "react"
import {
    SearchField,
    SearchInput,
    SearchIcon,
    SuggestionsList,
    SuggestionItem,
    SelectedUser,
    MultiSelect,
    MultiSelectButton,
    MultiSelectList,
    MultiSelectItem,
    ChevronIcon,
} from "./administration.styled"
import {
    Card, CardTitle, CardHeaderRow, PageHead, Button, Pill,
    FormRow, FieldLabel, FormActions, Notice,
    DataTable, TableScroll, EmptyState, SpinnerContainer, Spinner,
} from "../ui";
import { getCurrentSearchUsers, getAllEtoile, getAdministrationRSSIList, postAdministrationAddRSSI, postAdministrationRemoveRSSI } from "../../api/internal/usersInternalApi";

export default function Administration() {
    const [searchValue, setSearchValue] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedStars, setSelectedStars] = useState([]);
    const [shakeSearch, setShakeSearch] = useState(false);
    const [shakeDropdown, setShakeDropdown] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [etoiles, setEtoiles] = useState([]);
    const [loadingEtoiles, setLoadingEtoiles] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [rssiList, setRssiList] = useState([]);
    const [loadingRssi, setLoadingRssi] = useState(true);

    const searchRef = useRef(null);
    const dropdownRef = useRef(null);
    const debounceRef = useRef(null);

    const fetchRSSIList = async () => {
        try {
            setLoadingRssi(true);
            const data = await getAdministrationRSSIList();
            setRssiList(data || []);
        } catch (error) {
            console.error("Erreur chargement liste RSSI", error);
        } finally {
            setLoadingRssi(false);
        }
    };

    useEffect(() => {
        const fetchEtoiles = async () => {
            try {
                setLoadingEtoiles(true);
                const data = await getAllEtoile();
                setEtoiles(data || []);
            } catch (error) {
                console.error("Erreur lors du chargement des étoiles", error);
                setEtoiles([]);
            } finally {
                setLoadingEtoiles(false);
            }
        };

        fetchEtoiles();
        fetchRSSIList();

        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchValue(value);
        setSelectedUser(null);
        setSuccessMessage("");

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (value.length > 2) {
            debounceRef.current = setTimeout(async () => {
                try {
                    const results = await getCurrentSearchUsers(value);
                    setSuggestions(results || []);
                    setShowSuggestions(true);
                } catch (error) {
                    console.error("Erreur recherche suggestions", error);
                    setSuggestions([]);
                    setShowSuggestions(false);
                }
            }, 300);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    };

    const selectSuggestion = (user) => {
        setSelectedUser({
            id: user.objectId || user.userPrincipalName,
            name: user.displayName,
            email: user.userPrincipalName,
            RSSIEtoile: user.RSSIEtoile && user.RSSIEtoile.length >= 1 ? user.RSSIEtoile.join(" / ") : ["Aucune étoile"]
        });
        setSearchValue(user.displayName);
        setSuggestions([]);
        setShowSuggestions(false);
    };

    const handleToggleStar = (star) => {
        setSelectedStars((prev) => {
            if (prev.includes(star)) {
                return prev.filter((s) => s !== star);
            }
            return [...prev, star];
        });
    };

    const isStarSelected = (star) => selectedStars.includes(star);

    const getDropdownLabel = () => {
        if (loadingEtoiles) return "Chargement...";
        if (etoiles.length === 0) return "Aucune étoile disponible";
        if (selectedStars.length === 0) return "Sélectionner les étoiles";
        if (selectedStars.length === 1) return selectedStars[0];
        return `${selectedStars.length} étoiles sélectionnées`;
    };

    const triggerShake = (setter) => {
        setter(true);
        setTimeout(() => setter(false), 500);
    };

    const handleSubmit = async () => {
        setSuccessMessage("");
        setErrorMessage("");

        if (!selectedUser) {
            triggerShake(setShakeSearch);
            return;
        }
        if (selectedStars.length === 0) {
            triggerShake(setShakeDropdown);
            return;
        }

        try {
            const controller = new AbortController();
            const response = await postAdministrationAddRSSI(controller.signal, selectedUser.id, selectedStars);

            if (response?.message) {
                setSuccessMessage(response.message);
            }

            setSelectedUser(null);
            setSearchValue("");
            setSelectedStars([]);
            fetchRSSIList();
        } catch (error) {
            setErrorMessage(error?.message || "Une erreur est survenue");
        }
    };

    const handleRemove = async () => {
        setSuccessMessage("");
        setErrorMessage("");

        if (!selectedUser) {
            triggerShake(setShakeSearch);
            return;
        }
        if (selectedStars.length === 0) {
            triggerShake(setShakeDropdown);
            return;
        }

        try {
            const controller = new AbortController();
            const response = await postAdministrationRemoveRSSI(controller.signal, selectedUser.id, selectedStars);

            if (response?.message) {
                setSuccessMessage(response.message);
            }

            setSelectedUser(null);
            setSearchValue("");
            setSelectedStars([]);
            fetchRSSIList();
        } catch (error) {
            setErrorMessage(error?.message || "Une erreur est survenue");
        }
    };

    return (
        <>
            <PageHead>
                <div>
                    <h2>Rôles RSSI</h2>
                    <p>Attribution du rôle RSSI et de son périmètre d'étoiles.</p>
                </div>
            </PageHead>

            <Card>
                <CardTitle>Attribuer des étoiles à un RSSI</CardTitle>

                <FormRow>
                    <FieldLabel>
                        Utilisateur
                        <small>Recherchez par nom ou adresse mail</small>
                    </FieldLabel>
                    <SearchField ref={searchRef} className={shakeSearch ? "invalid-input" : ""}>
                        <SearchInput
                            placeholder="Rechercher un utilisateur..."
                            value={searchValue}
                            onChange={handleInputChange}
                            autoComplete="off"
                            onFocus={() => {
                                if (suggestions.length > 0 && searchValue.length > 2) {
                                    setShowSuggestions(true);
                                }
                            }}
                        />
                        <SearchIcon className="bx bx-search-alt-2" />

                        {showSuggestions && Array.isArray(suggestions) && suggestions.length > 0 && (
                            <SuggestionsList>
                                {suggestions.map((user, index) => (
                                    <SuggestionItem
                                        key={user.id || index}
                                        onClick={() => selectSuggestion(user)}
                                    >
                                        {user.displayName} ({user.userPrincipalName})
                                    </SuggestionItem>
                                ))}
                            </SuggestionsList>
                        )}

                    </SearchField>

                    {/* Hors de SearchField : l'icône de recherche est centrée sur
                        toute sa hauteur, ce bloc la décentrerait. */}
                    {selectedUser && (
                        <SelectedUser>
                            <i className="bx bx-user-check" />
                            {selectedUser.name} — {selectedUser.email} · {selectedUser.RSSIEtoile}
                        </SelectedUser>
                    )}
                </FormRow>

                <FormRow>
                    <FieldLabel>
                        Étoiles
                        <small>Périmètre accordé au RSSI</small>
                    </FieldLabel>
                    <MultiSelect ref={dropdownRef} className={shakeDropdown ? "invalid-input" : ""}>
                        <MultiSelectButton
                            type="button"
                            onClick={() => {
                                if (!loadingEtoiles && etoiles.length > 0) {
                                    setDropdownOpen(!dropdownOpen);
                                }
                            }}
                            disabled={loadingEtoiles || etoiles.length === 0}
                        >
                            <span>{getDropdownLabel()}</span>
                            <ChevronIcon className="bx bx-chevron-down" $isOpen={dropdownOpen} />
                        </MultiSelectButton>

                        {dropdownOpen && (
                            <MultiSelectList>
                                {etoiles.map((star, index) => (
                                    <MultiSelectItem
                                        key={index}
                                        $selected={isStarSelected(star)}
                                        onClick={() => handleToggleStar(star)}
                                    >
                                        <i className={isStarSelected(star) ? "bx bx-check-square" : "bx bx-square"} />
                                        {star}
                                    </MultiSelectItem>
                                ))}
                            </MultiSelectList>
                        )}
                    </MultiSelect>
                </FormRow>

                <FormActions>
                    <Button type="button" onClick={handleSubmit}>
                        <i className="bx bx-plus" />
                        Ajouter le RSSI
                    </Button>
                    <Button type="button" $variant="danger" onClick={handleRemove}>
                        <i className="bx bx-trash" />
                        Retirer les étoiles
                    </Button>
                </FormActions>

                {successMessage && (
                    <Notice $tone="ok" style={{ marginTop: '16px' }}>
                        <i className="bx bx-check-circle" />
                        {successMessage}
                    </Notice>
                )}

                {errorMessage && (
                    <Notice $tone="danger" style={{ marginTop: '16px' }}>
                        <i className="bx bx-error-circle" />
                        {errorMessage}
                    </Notice>
                )}
            </Card>

            <Card $pad="0">
                <CardHeaderRow style={{ padding: '14px 16px 0', marginBottom: '10px' }}>
                    <h2>Droits RSSI en cours</h2>
                    {!loadingRssi && rssiList.length > 0 && (
                        <Pill $tone="rssi">{rssiList.length}</Pill>
                    )}
                </CardHeaderRow>

                {loadingRssi ? (
                    <SpinnerContainer><Spinner /></SpinnerContainer>
                ) : rssiList.length === 0 ? (
                    <EmptyState>
                        <i className="bx bx-shield-quarter" />
                        <h3>Aucun RSSI</h3>
                        <p>Aucun compte ne porte actuellement le rôle RSSI.</p>
                    </EmptyState>
                ) : (
                    <TableScroll>
                        <DataTable>
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Mail</th>
                                    <th>Étoiles</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rssiList.map((user, i) => (
                                    <tr key={i}>
                                        <td>{user.displayName}</td>
                                        <td>{user.mail || user.userPrincipalName}</td>
                                        <td style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {(user.RSSIEtoile || []).map(e => (
                                                <Pill key={e} $tone="neutral">{e}</Pill>
                                            ))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </DataTable>
                    </TableScroll>
                )}
            </Card>
        </>
    );
}
